import { z } from 'zod';
import {
  currencySchema,
  displayNameSchema,
  emojiSchema,
  idSchema,
  isoDateTimeSchema,
  moneySchema,
} from './common';
import { paymentHandlesSchema, userPublicSchema } from './users';

export const groupRoleSchema = z.enum(['admin', 'member']);
export type GroupRole = z.infer<typeof groupRoleSchema>;

/** `direct` = the hidden two-person group behind 1:1 expenses (DESIGN 4.14). */
export const groupKindSchema = z.enum(['group', 'direct']);
export type GroupKind = z.infer<typeof groupKindSchema>;

/** The caller's position in a group: converted to the group currency plus raw per currency. */
export const memberBalanceSchema = z.object({
  /** Net in the group currency (positive = owed to you). Converted with each expense's stored rate. */
  converted: moneySchema,
  /** Real per-currency nets, non-zero only, never converted. */
  byCurrency: z.array(moneySchema),
});
export type MemberBalance = z.infer<typeof memberBalanceSchema>;

export const groupSummarySchema = z.object({
  id: idSchema,
  kind: groupKindSchema,
  name: z.string(),
  emoji: z.string().nullable(),
  defaultCurrency: z.string(),
  simplifyDebts: z.boolean(),
  archived: z.boolean(),
  /** The caller muted this group (no push, no badge; inbox rows kept). */
  muted: z.boolean(),
  memberCount: z.int(),
  /** Non-deleted expenses in the group (e.g. for the delete-group confirmation). */
  expenseCount: z.int(),
  myMemberId: idSchema,
  myRole: groupRoleSchema,
  myBalance: memberBalanceSchema,
  createdAt: isoDateTimeSchema,
  lastActivityAt: isoDateTimeSchema,
});
export type GroupSummary = z.infer<typeof groupSummarySchema>;
export const groupListSchema = z.object({ items: z.array(groupSummarySchema) });
export type GroupList = z.infer<typeof groupListSchema>;

export const memberSchema = z.object({
  /** Member id: what expenses, splits and settlements reference. */
  id: idSchema,
  groupId: idSchema,
  /** `null` for placeholders ("Not joined"). */
  userId: idSchema.nullable(),
  /** Account display name, or the placeholder name. */
  name: z.string(),
  avatarEmoji: z.string().nullable(),
  avatarColor: z.int().nullable(),
  role: groupRoleSchema,
  isPlaceholder: z.boolean(),
  /** Removed or left; kept so history still resolves names. */
  removed: z.boolean(),
  /** The account is disabled by the app admin (still in the group, cannot sign in). */
  disabled: z.boolean(),
  joinedAt: isoDateTimeSchema,
  /** Visible to people who share a group with this user (DESIGN assumption 8). */
  paymentHandles: paymentHandlesSchema.nullable(),
  /** The account's default currency (e.g. to settle up in it); `null` for placeholders. */
  defaultCurrency: z.string().nullable(),
});
export type Member = z.infer<typeof memberSchema>;

export const groupDetailSchema = groupSummarySchema.extend({
  createdBy: idSchema,
  /** Active and removed members (filter on `removed`). */
  members: z.array(memberSchema),
  /** Token for `/join/:token`, shareable by any member. */
  joinToken: z.string(),
  joinUrl: z.string(),
});
export type GroupDetail = z.infer<typeof groupDetailSchema>;

export const createGroupRequestSchema = z.object({
  /** Optional client-generated id (offline-first); retries with the same id are idempotent. */
  id: idSchema.optional(),
  name: z.string().trim().min(1).max(50),
  emoji: emojiSchema.nullable().optional(),
  defaultCurrency: currencySchema,
  simplifyDebts: z.boolean().optional(),
});
export type CreateGroupRequest = z.input<typeof createGroupRequestSchema>;

/** `PATCH /api/groups/:id` (group admin). */
export const updateGroupRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(50),
    emoji: emojiSchema.nullable(),
    defaultCurrency: currencySchema,
    simplifyDebts: z.boolean(),
    archived: z.boolean(),
  })
  .partial();
export type UpdateGroupRequest = z.input<typeof updateGroupRequestSchema>;

/** `PUT /api/groups/:id/mute` (per user). */
export const muteGroupRequestSchema = z.object({ muted: z.boolean() });
export type MuteGroupRequest = z.input<typeof muteGroupRequestSchema>;

export const joinTokenResponseSchema = z.object({ joinToken: z.string(), joinUrl: z.string() });
export type JoinTokenResponse = z.infer<typeof joinTokenResponseSchema>;

/** `GET /api/join/:token`: preview for the join screen (signed-in users only). */
export const joinPreviewSchema = z.object({
  group: z.object({
    id: idSchema,
    name: z.string(),
    emoji: z.string().nullable(),
    defaultCurrency: z.string(),
    memberCount: z.int(),
  }),
  /** Who created / last reset the link ("Invited by Ana"). */
  invitedBy: userPublicSchema.nullable(),
  members: z.array(
    z.object({
      id: idSchema,
      name: z.string(),
      avatarEmoji: z.string().nullable(),
      avatarColor: z.int().nullable(),
      isPlaceholder: z.boolean(),
    }),
  ),
  alreadyMember: z.boolean(),
});
export type JoinPreview = z.infer<typeof joinPreviewSchema>;

/**
 * `POST /api/join/:token`. `claimMemberId` = "I'm Marta" (a placeholder of the group);
 * omit or `null` = "add me as a new member".
 */
export const joinGroupRequestSchema = z.object({
  claimMemberId: idSchema.nullable().optional(),
});
export type JoinGroupRequest = z.input<typeof joinGroupRequestSchema>;
export const joinGroupResponseSchema = z.object({ groupId: idSchema, memberId: idSchema });
export type JoinGroupResponse = z.infer<typeof joinGroupResponseSchema>;

/**
 * `POST /api/groups/:id/members`: `{ name }` adds a placeholder (person without an account);
 * `{ userId }` adds an existing account you already share a group with (they get a
 * "added to a group" notification).
 */
export const addMemberRequestSchema = z.union([
  z.object({ name: displayNameSchema }),
  z.object({ userId: idSchema }),
]);
export type AddMemberRequest = z.input<typeof addMemberRequestSchema>;
/** @deprecated Use `addMemberRequestSchema`. */
export const addPlaceholderRequestSchema = addMemberRequestSchema;
export type AddPlaceholderRequest = AddMemberRequest;

/** `PATCH /api/groups/:id/members/:memberId`: rename a placeholder. */
export const renamePlaceholderRequestSchema = z.object({ name: displayNameSchema });
export type RenamePlaceholderRequest = z.input<typeof renamePlaceholderRequestSchema>;

/**
 * `POST /api/groups/:id/members/:memberId/link` (group admin): link a placeholder to an
 * account. If that account is already a member, the two members are merged.
 */
export const linkPlaceholderRequestSchema = z.object({ userId: idSchema });
export type LinkPlaceholderRequest = z.input<typeof linkPlaceholderRequestSchema>;

export const memberResponseSchema = z.object({ member: memberSchema });
export type MemberResponse = z.infer<typeof memberResponseSchema>;

/** `POST /api/direct`: get or create the hidden 1:1 group with another user. */
export const directGroupRequestSchema = z.object({ userId: idSchema });
export type DirectGroupRequest = z.input<typeof directGroupRequestSchema>;
